import React from 'react';

import { MenuItem } from 'components';

import { useTranslation } from 'third-party';

import { useCache } from 'modules/cache';

import { ListFilter } from 'components/Table/Filtering/ListFilter';
import { TypeaheadFilter } from 'components/Table/Filtering/TypeaheadFilter';
import { ColumnFilteringProps, TableColumn } from 'components/Table/types';

import { camelCaseHumanized } from 'utils/app';
import { getEnumValues } from 'utils/helper';

import {
  APIResponse,
  APIUser,
  APIUserRight,
  APIUserTenant,
  APIUserType,
} from 'types/api';
import { TypeaheadOption } from 'types/app';

export const UserTypeFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      <MenuItem value={APIUserType.Admin}>Admin</MenuItem>
      <MenuItem value={APIUserType.User}>User</MenuItem>
      <MenuItem value={APIUserType.Editor}>Editor</MenuItem>
    </ListFilter>
  );
};

export const TenantFilter: React.FC<ColumnFilteringProps> = props => {
  const { tenantTypeahead } = useCache();

  const loadTenants = async (value: string) => {
    const result: APIResponse<{ name: string; id: string }[]> =
      await tenantTypeahead(value);
    let options: TypeaheadOption[] = [];
    if (!result.error) {
      options = result.payload.map(it => ({
        label: it.name,
        value: it.id,
      }));
    }
    return {
      options,
    };
  };

  return <TypeaheadFilter {...props} loadOptions={loadTenants} />;
};

export const UserRightStatusFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {getEnumValues(APIUserRight).map(right => (
        <MenuItem key={right} value={right}>
          {t(`membersList.userRights.${right}`)}
        </MenuItem>
      ))}
    </ListFilter>
  );
};

export const userColumns = (tenantId: string): TableColumn<APIUser>[] => [
  {
    dataId: 'id',
    label: 'id',
    minWidth: 80,
    width: 140,
    hidden: true,
    sortable: true,
    resizable: true,
  },
  {
    dataId: 'firstName',
    label: 'membersList.column.firstName',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'lastName',
    label: 'membersList.column.lastName',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'email',
    label: 'membersList.column.email',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'phone',
    label: 'membersList.column.phone',
    minWidth: 80,
    width: 140,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'userRights',
    label: 'membersList.column.userRights',
    formatter: (value: APIUserTenant[], item: APIUser) => {
      return {
        value: item.userTenants
          .filter(it => it.tenant.id == tenantId)
          .flatMap(it => it?.userRights || [])
          .map(it => (it.right ? camelCaseHumanized(it.right) : ''))
          .join(', '),
      };
    },
    minWidth: 80,
    width: 140,
    sortable: false,
    resizable: true,
    customFilter: UserRightStatusFilter,
  },
  {
    dataId: 'userType',
    label: 'membersList.column.userType',
    formatter: (value: any, item: APIUser) => ({
      value: item.userTenants
        .filter(it => it.tenant.id == tenantId)
        .map(it => it.userType)
        .join(', '),
    }),
    minWidth: 80,
    width: 140,
    sortable: false,
    resizable: true,
    customFilter: UserTypeFilter,
  },
];
